"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { authClient } from "@/lib/auth-client";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaSpinner,
} from "react-icons/fa";

export default function SignInPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError("Email is required");
      return false;
    }
    if (!formData.password.trim()) {
      setError("Password is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      // Sign in with Better Auth
      const result = await authClient.signIn.email({
        email: formData.email,
        password: formData.password,
      });

      if (result.error) {
        setError(result.error.message || "Invalid email or password");
        return;
      }

      // Check if user is a platform admin - redirect to admin dashboard
      const userResponse = await fetch("/api/auth/session");
      const userData = await userResponse.json();

      if (userData.user?.role === "super_admin" || userData.user?.role === "platform_admin") {
        // Redirect platform admins to admin dashboard
        const isDev = window.location.hostname === "localhost";
        if (isDev) {
          router.push("/platform-admin");
        } else {
          window.location.href = "https://admin.kuttostechnologies.com";
        }
        return;
      }

      // Fetch user's brand to redirect to their POS
      const brandResponse = await fetch("/api/auth/user-brand");
      const brandData = await brandResponse.json();

      if (brandData.brand) {
        // Redirect to POS with brand context
        const isDev = window.location.hostname === "localhost";
        if (isDev) {
          router.push(`/pos?brand=${brandData.brand.subdomain}&industry=${brandData.brand.industry}`);
        } else {
          // In production, redirect to subdomain
          const domainMap: Record<string, string> = {
            fashion: "kuttosfashion.com",
            electronics: "kuttostechnologies.com",
            retail: "kuttosretail.com",
          };
          const domain = domainMap[brandData.brand.industry] || "kuttosfashion.com";
          window.location.href = `https://${brandData.brand.subdomain}.${domain}/pos`;
        }
      } else {
        // User has no brand - redirect to onboarding or show message
        setError("No brand found for this account. Please contact support.");
      }
    } catch (err: any) {
      console.error("Sign in error:", err);
      setError(err.message || "Failed to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030305] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-kuttos-purple/20 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-kuttos-blue/15 rounded-full blur-[120px]"></div>
        <div className="absolute inset-0 bg-grid opacity-20"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Card */}
        <div className="glass-panel rounded-2xl p-8 border border-white/10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <Image
                src="/images/kuttoslogo.png"
                alt="Kuttos Technologies"
                width={360}
                height={120}
                className="h-[120px] w-auto"
              />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome back</h1>
            <p className="text-gray-400">Sign in to your account</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Email
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-kuttos-cyan/50 focus:ring-1 focus:ring-kuttos-cyan/50 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300"
                >
                  Password
                </label>
                <Link
                  href="/auth/forgot-password"
                  className="text-sm text-kuttos-cyan hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-kuttos-cyan/50 focus:ring-1 focus:ring-kuttos-cyan/50 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-cyan py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{" "}
            <Link
              href="/auth/signup"
              className="text-kuttos-cyan hover:underline"
            >
              Sign up
            </Link>
          </p>
        </div>

        {/* Back Link - Centered below card */}
        <div className="flex justify-center mt-6">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <FaArrowLeft className="text-sm" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
